import { useCallback, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatMessageEnvelope } from '@bliks-io/contracts';
import {
  AiThinkingIndicator,
  CHAT_COLUMN_MEASURE,
  ChatPartNotice,
  cn,
  Marker,
  MessageAvatar,
  MessageScroller,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
  Skeleton,
  useMessageScroller,
  useReducedMotion,
  type ChatFeedbackHandler
} from '@bliks-io/ui';
import { ChatMessage } from './ChatMessage';
import { hasVisibleContent, type TranscriptPart } from './parts';
import { ErrorBoundary } from '@/components/error-boundary';
import { samplePhases } from '@/lib/ai-thinking-phases';

/**
 * The message-level metadata a turn carries, taken straight from the persisted
 * envelope so the transcript can never fall behind what the backend returns:
 * `entityRefs` (source chips), `langfuseTraceId` (feedback scoring),
 * `aiProvenance` (the EU AI-Act mark — present only when a model generated the
 * message, so it, not the role, decides the `data-ai-generated` attribute) and
 * `userFeedback` (the reader's standing thumbs verdict).
 */
export type ChatMessageMetadata = Pick<
  ChatMessageEnvelope,
  'entityRefs' | 'langfuseTraceId' | 'aiProvenance' | 'userFeedback'
>;

/**
 * Transcript turn, as emitted by `convertToMessageList`: parts plus metadata.
 */
export interface ChatTranscriptMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  parts?: TranscriptPart[];
  metadata?: ChatMessageMetadata;
}

/**
 * Distance (px) from the bottom within which the transcript keeps following new
 * content. Anything further up means the reader is reading back, so streaming
 * must not yank them down.
 */
const SCROLL_EDGE_THRESHOLD = 100;

/** The thinking row is a scroller item like any other and needs a stable id. */
const THINKING_INDICATOR_ID = 'chat-thinking-indicator';

/**
 * Whether a turn has anything worth putting on screen. Assistant turns that
 * have not produced their first token yet are suppressed in favour of the
 * thinking indicator. Which parts count is owned by `./parts` (ADR 0025), so
 * new part kinds widen it there rather than here — a chart-only turn must not
 * be dropped.
 */
function hasRenderablePart(message: ChatTranscriptMessage): boolean {
  return message.parts?.some(hasVisibleContent) ?? false;
}

/**
 * The indicator covers the gap between sending and the first visible token: no
 * turns yet, the user's turn still unanswered, or an assistant turn that has
 * started streaming but rendered nothing.
 */
function shouldShowLoadingIndicator(messages: ChatTranscriptMessage[]): boolean {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage) return true;

  return (
    lastMessage.role === 'user' ||
    (lastMessage.role === 'assistant' && !hasRenderablePart(lastMessage))
  );
}

interface ChatMessagesProps {
  messages: ChatTranscriptMessage[];
  isLoading?: boolean;
  isHistoryLoading?: boolean;
  assistantIcon: ReactNode;
  userAvatar?: string;
  /**
   * Ordered list of pre-translated phrase buckets (start / middle / end).
   * Each time the thinking indicator mounts, one phrase is sampled per
   * bucket — keeps the progression narratively coherent while letting the
   * wording vary across requests.
   */
  thinkingPhaseBuckets: string[][];
  className?: string;
  /** Organization slug for entity links */
  slug?: string;
  /**
   * Submits a thumbs verdict on one answer. Omitting it leaves the transcript
   * exactly as it was before feedback existed. MUST be referentially stable —
   * `ChatMessage` is memoised.
   */
  onFeedback?: ChatFeedbackHandler;
  /**
   * Whether this particular turn's ID names a row the host's backend could
   * actually write to. The predicate is the HOST's, so the transcript stays
   * agent-agnostic: a Bliksy turn qualifies only once its id is the canonical
   * stored one (a live-streamed answer carries a client-minted id no server
   * write could match). Omitting it means "every turn".
   */
  canVoteMessage?: (message: ChatTranscriptMessage) => boolean;
}

/**
 * One turn, isolated from the rest of the transcript.
 *
 * The nearest boundary above this is `AppLayout`'s, which wraps the whole route
 * — so a throw inside one message part takes the transcript AND the composer
 * with it, leaving a page whose sidebar and header still work but whose chat is
 * gone. Per turn, the same throw is one broken bubble.
 *
 * What made this worth adding now: embedded record views render `@xyflow/react`
 * and `elkjs` INLINE in the transcript, against records fetched live. A view
 * whose payload has skewed from what the canvas expects is a throw in the
 * middle of an answer, not a failed request the renderer can degrade.
 */
function ChatTurnBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  // No retry offered: a skewed part throws again on the next render, so a button
  // that promises otherwise would be a lie about what it can fix.
  return (
    <ErrorBoundary fallback={() => <ChatPartNotice message={t('chat.messageFailed')} />}>
      {children}
    </ErrorBoundary>
  );
}

function ChatThinkingIndicator({
  buckets,
  assistantIcon
}: {
  buckets: string[][];
  assistantIcon: ReactNode;
}) {
  const [phases, setPhases] = useState(() => samplePhases(...buckets));
  return (
    <Marker className="items-start">
      <MessageAvatar variant="icon">{assistantIcon}</MessageAvatar>
      <AiThinkingIndicator
        phases={phases}
        variant="inline"
        onCycle={() => setPhases(samplePhases(...buckets))}
        className="py-1"
      />
    </Marker>
  );
}

/**
 * Sending a message must always bring the transcript back to the end, smoothly,
 * even when the reader had scrolled far up — the scroller's own autoscroll only
 * follows the bottom when the reader is already near it. Streaming token
 * updates are deliberately left to that automatic path so they stay instant
 * instead of firing a smooth jump per token.
 *
 * The jump rides on this node attaching to the DOM rather than on an effect:
 * the caller keys it on the transcript length, so React mounts a fresh node in
 * exactly the commit that appends a turn, and everything that leaves the turn
 * count alone — a streamed token, a history refetch that renumbers message ids
 * — leaves the mounted node untouched and silent.
 */
function ScrollToEndOnMount() {
  const { scrollToEnd } = useMessageScroller();
  const prefersReducedMotion = useReducedMotion();

  // Stable, so React attaches it once per mount instead of re-running it on
  // every render the way an inline ref callback would.
  const revealNewestTurn = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) scrollToEnd({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    },
    [scrollToEnd, prefersReducedMotion]
  );

  return <div hidden ref={revealNewestTurn} />;
}

/**
 * Shared scrollable container for chat messages, built on the shadcn
 * `MessageScroller` primitive. The scroller viewport is the only element that
 * scrolls the transcript — both hosts constrain their height instead.
 */
export function ChatMessages({
  messages,
  isLoading,
  isHistoryLoading,
  assistantIcon,
  userAvatar,
  thinkingPhaseBuckets,
  className,
  slug,
  onFeedback,
  canVoteMessage
}: ChatMessagesProps) {
  const { t } = useTranslation();

  // Show skeleton loading state when fetching chat history
  if (isHistoryLoading) {
    return (
      <div className={cn('min-h-0 flex-1 overflow-y-auto bg-background px-4 py-6', className)}>
        <div className="flex flex-col gap-6">
          {/* User message skeleton */}
          <div className="flex gap-2 justify-end">
            <div className="flex flex-col gap-2 items-end max-w-[70%]">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
          </div>
          {/* Assistant message skeleton */}
          <div className="flex gap-2">
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
            <div className="flex flex-col gap-2 max-w-[70%]">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-80" />
              <Skeleton className="h-4 w-56" />
            </div>
          </div>
          {/* Another user message skeleton */}
          <div className="flex gap-2 justify-end">
            <div className="flex flex-col gap-2 items-end max-w-[70%]">
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
          </div>
          {/* Another assistant message skeleton */}
          <div className="flex gap-2">
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
            <div className="flex flex-col gap-2 max-w-[70%]">
              <Skeleton className="h-4 w-72" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const lastMessage = messages[messages.length - 1];
  const awaitingAnswer = lastMessage?.role === 'user';

  // Only the answer still being written loses its thumbs row — it has no stored
  // turn to vote on yet. Suppressing feedback transcript-wide while `isLoading`
  // would unmount every row on each turn, jumping the scroll position and
  // destroying an open comment popover mid-typing.
  const streamingMessageId =
    isLoading && lastMessage?.role === 'assistant' ? lastMessage.id : undefined;

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col bg-background', className)}>
      <MessageScrollerProvider
        autoScroll
        defaultScrollPosition="end"
        scrollEdgeThreshold={SCROLL_EDGE_THRESHOLD}
      >
        <MessageScroller>
          <MessageScrollerViewport preserveScrollOnPrepend aria-label={t('chat.transcript')}>
            {/* The centred cap keeps a full-page transcript at a readable
                measure on wide screens; inside the narrow Bliksy panel it
                never binds. */}
            <MessageScrollerContent className={cn(CHAT_COLUMN_MEASURE, 'gap-6 px-4 py-6')}>
              {messages.map((message) => {
                // Only render user and assistant messages
                if (message.role !== 'user' && message.role !== 'assistant') {
                  return null;
                }

                // Assistant turns with no text yet are covered by the indicator
                if (message.role === 'assistant' && !hasRenderablePart(message)) {
                  return null;
                }

                // The whole gate lives here, so `ChatMessage` only has to answer
                // "am I wired for feedback?". The last clause is what makes a
                // vote cast under telemetry survive it being turned off: the
                // backend stores the verdict in Mongo either way, so a stored
                // one must stay visible — and retractable — with no trace to
                // score against.
                const votable =
                  message.role === 'assistant' &&
                  message.id !== streamingMessageId &&
                  (canVoteMessage?.(message) ?? true) &&
                  Boolean(message.metadata?.langfuseTraceId || message.metadata?.userFeedback);

                return (
                  <MessageScrollerItem key={message.id} messageId={message.id}>
                    <ChatTurnBoundary>
                      <ChatMessage
                        role={message.role}
                        parts={message.parts}
                        entityRefs={message.metadata?.entityRefs}
                        aiProvenance={message.metadata?.aiProvenance}
                        messageId={message.id}
                        userFeedback={message.metadata?.userFeedback}
                        onFeedback={votable ? onFeedback : undefined}
                        assistantIcon={assistantIcon}
                        userAvatar={userAvatar}
                        slug={slug}
                      />
                    </ChatTurnBoundary>
                  </MessageScrollerItem>
                );
              })}

              {/* Loading indicator - show when waiting for response to start */}
              {isLoading && shouldShowLoadingIndicator(messages) && (
                <MessageScrollerItem messageId={THINKING_INDICATOR_ID}>
                  <ChatThinkingIndicator
                    buckets={thinkingPhaseBuckets}
                    assistantIcon={assistantIcon}
                  />
                </MessageScrollerItem>
              )}
            </MessageScrollerContent>
          </MessageScrollerViewport>
        </MessageScroller>
        {awaitingAnswer && <ScrollToEndOnMount key={messages.length} />}
      </MessageScrollerProvider>
    </div>
  );
}
